/**
 * Shared video player behaviour: Vimeo embedding, the start-only play button and
 * the expand/theater overlay. Used by interact/video-demo and
 * interact/campaign-hero-video, which differ only in their class prefix and in
 * what pressing play does while the video is still gated.
 */

export interface PlayerOptions {
  /** BEM block prefix, e.g. `video-demo`. */
  prefix: string;
  /** Called instead of playing when the block is still locked. */
  onLockedPlay?: () => void;
}

export function initPlayer(block: HTMLElement, { prefix, onLockedPlay }: PlayerOptions): void {
  const videoBox = block.querySelector<HTMLElement>(`.${prefix}__video`);
  const video = block.querySelector<HTMLVideoElement>(`video.${prefix}__media`);
  const playBtn = block.querySelector<HTMLButtonElement>(`.${prefix}__play`);

  const vimeoId = videoBox?.dataset.vimeoId || '';

  let lastTime = 0;
  let inlineFrame: HTMLIFrameElement | null = null;
  let theaterFrame: HTMLIFrameElement | null = null;
  let inlineReady = false;
  let pauseInlineWhenReady = false;

  const postToVimeo = (frame: HTMLIFrameElement | null, method: string, value?: unknown): void => {
    frame?.contentWindow?.postMessage(
      JSON.stringify(value === undefined ? { method } : { method, value }),
      '*',
    );
  };

  // Pause now (works once the player is ready); a pause sent before ready is
  // dropped by Vimeo, so also re-issue it when the ready event arrives.
  const pauseInline = (): void => {
    postToVimeo(inlineFrame, 'pause');
    if (!inlineReady) pauseInlineWhenReady = true;
  };

  const createVimeoIframe = (startAt = 0): HTMLIFrameElement => {
    const frame = document.createElement('iframe');
    frame.className = `${prefix}__iframe`;
    let src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&api=1&title=0&byline=0&portrait=0&dnt=1`;
    if (startAt > 0) src += `#t=${Math.floor(startAt)}s`;
    frame.src = src;
    frame.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('title', 'Product demo video');
    return frame;
  };

  if (vimeoId) {
    // Only react to this block's own player(s), not other Vimeo embeds.
    window.addEventListener('message', (event: MessageEvent) => {
      if (
        event.source !== inlineFrame?.contentWindow &&
        event.source !== theaterFrame?.contentWindow
      ) {
        return;
      }
      let msg: { event?: string; method?: string; value?: number };
      try {
        msg = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (msg?.event === 'ready' && event.source === inlineFrame?.contentWindow) {
        inlineReady = true;
        if (pauseInlineWhenReady) {
          postToVimeo(inlineFrame, 'pause');
          pauseInlineWhenReady = false;
        }
      } else if (msg?.method === 'getCurrentTime' && typeof msg.value === 'number') {
        // Direct query response — the reliable way to read the current position.
        lastTime = msg.value;
      }
    });
  }

  const startVimeo = (): void => {
    if (!videoBox || !vimeoId || inlineFrame) return;
    inlineFrame = createVimeoIframe();
    videoBox.appendChild(inlineFrame);
    // Our branded button is start-only; Vimeo's own controls take over now.
    videoBox.dataset.started = 'true';
  };

  if (playBtn) {
    const play = (): void => {
      if (block.dataset.state !== 'unlocked') {
        onLockedPlay?.();
        return;
      }
      // Focus must leave this button before it hides or the page scrolls; park it on
      // the player so keyboard users keep their place.
      if (videoBox) {
        videoBox.setAttribute('tabindex', '-1');
        videoBox.focus({ preventScroll: true });
      } else {
        playBtn.blur();
      }
      if (vimeoId) {
        startVimeo();
        return;
      }
      const promise = video?.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {});
      }
    };
    playBtn.addEventListener('click', play);
  }

  if (video) {
    // Once playback starts, hand off to the native controls (start-only button).
    video.addEventListener('play', () => {
      if (videoBox) videoBox.dataset.started = 'true';
    });
    // Drop the poster only once real frames render, so a still-buffering
    // (preload="none") video doesn't flash black when playback is requested.
    video.addEventListener('playing', () => {
      video.removeAttribute('poster');
    });
  }

  const expandBtn = block.querySelector<HTMLButtonElement>('[data-video-expand]');
  const theater = block.querySelector<HTMLElement>('[data-video-theater]');
  const theaterSlot = block.querySelector<HTMLElement>('[data-video-theater-slot]');
  const theaterClose = block.querySelector<HTMLButtonElement>('[data-video-theater-close]');

  if (expandBtn && theater && theaterSlot && videoBox) {
    let homeParent: HTMLElement | null = null;
    let homeNext: Element | null = null;

    const showOverlay = (): void => {
      document.body.appendChild(theater);
      theater.classList.add('is-active');
      theater.setAttribute('aria-hidden', 'false');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      theaterClose?.focus();
    };

    const openTheater = (): void => {
      if (vimeoId) {
        // Ask the inline player for its exact position, then open a fresh iframe
        // seeded to it (moving the inline iframe would reload/restart it).
        postToVimeo(inlineFrame, 'getCurrentTime');
        window.setTimeout(() => {
          if (!theaterFrame) {
            theaterFrame = createVimeoIframe(lastTime);
            theaterSlot.appendChild(theaterFrame);
          }
          pauseInline();
          showOverlay();
        }, 200);
        return;
      }
      if (video) {
        // The same element moves, so currentTime is preserved; resume if it was
        // playing (reparenting can pause it in some browsers).
        const wasPlaying = !video.paused;
        homeParent = video.parentElement;
        homeNext = video.nextElementSibling;
        theaterSlot.appendChild(video);
        if (wasPlaying) video.play().catch(() => {});
        showOverlay();
      }
    };

    const closeTheater = (): void => {
      if (!theater.classList.contains('is-active')) return;
      if (vimeoId) {
        // Read the theater's position, then pause it immediately so its audio
        // stops the moment the overlay hides (the frame is removed after the
        // response has had time to land). Then resume the inline player there.
        postToVimeo(theaterFrame, 'getCurrentTime');
        postToVimeo(theaterFrame, 'pause');
        window.setTimeout(() => {
          theaterFrame?.remove();
          theaterFrame = null;
          if (lastTime > 0) postToVimeo(inlineFrame, 'setCurrentTime', lastTime);
          postToVimeo(inlineFrame, 'play');
        }, 200);
      } else if (video && homeParent) {
        const wasPlaying = !video.paused;
        homeParent.insertBefore(video, homeNext);
        if (wasPlaying) video.play().catch(() => {});
      }
      theater.classList.remove('is-active');
      theater.setAttribute('aria-hidden', 'true');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      expandBtn.focus();
    };

    expandBtn.addEventListener('click', openTheater);
    theaterClose?.addEventListener('click', closeTheater);
    theater.addEventListener('click', (event) => {
      if (event.target === theater) closeTheater();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeTheater();
    });
  }
}
